import {
  DEFAULT_MEETING_TIMEZONE,
  normalizeRescheduleTimeLabel,
  parseRescheduleSuggestionLabel,
  resolveRescheduleTimezoneName,
} from "./meetingScheduleUtils";

const LIVE_MEET_BOOKING_PROVIDERS = new Set(["live_meet", "search_job_meet"]);
const LIVE_MEET_LABEL = "Search Job Meet";
const INSTANT_LIVE_MEET_LABEL = "Instant Search Job Meet";
const LIVE_MEET_ACTIVE_LABEL = "Active";
const CANCELLED_STATUSES = new Set([
  "cancelled",
  "canceled",
]);
const DECLINED_STATUSES = new Set([
  "declined",
  "declined_by_candidate",
  "declined_by_interviewer",
]);
const COMPLETED_STATUSES = new Set(["completed", "ended", "closed"]);
const RESCHEDULE_PENDING_STATUSES = new Set([
  "reschedule_requested_by_candidate",
  "reschedule_requested_by_interviewer",
  "next_slots_shared_by_interviewer",
]);
const ACCEPTANCE_STATUS_CONFIG = {
  pending_acceptance: {
    key: "awaiting_confirmation",
    label: "Awaiting confirmation",
    helper: "Waiting for the candidate and interviewer to confirm this interview.",
  },
  accepted_by_candidate: {
    key: "confirmed_by_candidate",
    label: "Confirmed by candidate",
    helper:
      "The candidate confirmed this interview. Waiting for the interviewer to confirm.",
  },
  accepted_by_interviewer: {
    key: "confirmed_by_interviewer",
    label: "Confirmed by interviewer",
    helper:
      "The interviewer confirmed this interview. Waiting for the candidate to confirm.",
  },
};
const ENDED_ROOM_STATUSES = new Set([
  "closed",
  "ended",
  "completed",
  "cancelled",
  "canceled",
]);
const STARTING_SOON_WINDOW_MS = 30 * 60 * 1000;

const normalizeLower = (value) => String(value || "").trim().toLowerCase();
const HAS_TIMEZONE_SUFFIX = /(?:Z|[+-]\d{2}:\d{2})$/i;
const ISO_TIMESTAMP_PREFIX = /^\d{4}-\d{2}-\d{2}T/;

const normalizeUtcIsoValue = (value) => {
  if (typeof value !== "string") return value;
  const trimmed = value.trim();
  if (!trimmed) return trimmed;
  if (ISO_TIMESTAMP_PREFIX.test(trimmed) && !HAS_TIMEZONE_SUFFIX.test(trimmed)) {
    return `${trimmed}Z`;
  }
  return trimmed;
};

const resolveDisplayTimezone = (timezoneLabel) => {
  const candidate = String(timezoneLabel || "").trim();
  if (!candidate) {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  }
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: candidate });
    return candidate;
  } catch {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  }
};

const asDate = (value) => {
  if (!value) return null;
  const date = new Date(normalizeUtcIsoValue(value));
  return Number.isNaN(date.getTime()) ? null : date;
};

const extractTimezoneFromSlotLabel = (slotLabel) => {
  const match = /\(([^)]+)\)\s*$/.exec(String(slotLabel || "").trim());
  return match?.[1] || "";
};

export const isLiveMeetBookingProvider = (provider) =>
  LIVE_MEET_BOOKING_PROVIDERS.has(normalizeLower(provider));

export const canSendLiveMeetConfirmationReminders = (booking) => {
  if (!booking) return false;
  const status = normalizeLower(booking.status);
  if (status === "confirmed") return false;
  if (
    booking.host_confirmation_override ||
    booking.confirmation_statuses?.host_override
  ) {
    return false;
  }
  const pending = booking.confirmation_statuses?.pending_participants;
  return Array.isArray(pending) && pending.length > 0;
};

export const isInterviewRoomReschedulePending = (booking) => {
  if (!booking) return false;
  const status = normalizeLower(booking?.status);
  const meetingOutcome = normalizeLower(booking?.meeting_outcome);
  const joinReason = normalizeLower(booking?.join_reason);
  return (
    meetingOutcome === "reschedule_pending" ||
    joinReason === "rescheduled" ||
    RESCHEDULE_PENDING_STATUSES.has(status) ||
    status.startsWith("reschedule")
  );
};

export const isInterviewRoomDeclined = (booking) =>
  DECLINED_STATUSES.has(normalizeLower(booking?.status));

export const getInterviewRoomTypeLabel = (booking) => {
  const schedulingMode = normalizeLower(booking?.scheduling_mode);
  if (["instant", "instant_live_meet"].includes(schedulingMode)) {
    return INSTANT_LIVE_MEET_LABEL;
  }
  if (schedulingMode === "manual") {
    return `Manual Scheduled ${LIVE_MEET_LABEL}`;
  }
  return `Automated Scheduled ${LIVE_MEET_LABEL}`;
};

export const getInterviewRoomHostJoinUrl = (booking) => {
  if (!booking || booking.can_join === false || isInterviewRoomReschedulePending(booking) || isInterviewRoomDeclined(booking)) {
    return "";
  }
  const roomId = booking.live_room_id || booking.live_room?.room_id;
  if (!roomId || typeof window === "undefined") {
    return "";
  }
  const params = new URLSearchParams();
  if (booking.booking_id) {
    params.set("booking_id", String(booking.booking_id));
  }
  const query = params.toString();
  return `${window.location.origin}/interview-room/${encodeURIComponent(roomId)}${query ? `?${query}` : ""}`;
};

export const getInterviewRoomJoinUrl = (booking, role = "interviewer") => {
  if (!booking || booking.can_join === false || isInterviewRoomReschedulePending(booking) || isInterviewRoomDeclined(booking)) {
    return "";
  }
  if (role === "host") {
    return getInterviewRoomHostJoinUrl(booking);
  }
  if (role === "candidate") {
    return (
      booking.candidate_invite_url ||
      booking.join_url ||
      booking.meet_link ||
      ""
    );
  }
  return (
    booking.interviewer_join_url ||
    booking.interviewer_url ||
    booking.join_url ||
    booking.meet_link ||
    ""
  );
};

export const getInterviewRoomShareUrl = (booking, role = "interviewer") => {
  if (!booking) return "";
  if (role === "candidate") {
    return getInterviewRoomJoinUrl(booking, "candidate");
  }
  if (role === "host") {
    return getInterviewRoomHostJoinUrl(booking);
  }
  return (
    getInterviewRoomJoinUrl(booking, "interviewer") ||
    booking.interviewer_join_url ||
    booking.interviewer_url ||
    booking.join_url ||
    booking.meet_link ||
    ""
  );
};

export const canViewInterviewRoomTranscript = (booking) => {
  if (!booking) return false;
  const hasTranscript =
    booking.has_transcript === true ||
    Number(booking.transcript_segment_count || 0) > 0;
  return (
    isLiveMeetBookingProvider(booking.meeting_provider) &&
    hasTranscript &&
    Boolean(booking.live_room_id || booking.booking_id)
  );
};

export const formatInterviewRoomSchedule = (booking) => {
  if (isInstantLiveMeetBooking(booking)) {
    const status = resolveInterviewRoomStatus(booking);
    const headline =
      status.key === "active"
        ? "Available now"
        : status.key === "completed"
          ? "Instant meet ended"
          : INSTANT_LIVE_MEET_LABEL;
    return {
      isInstant: true,
      scheduleKind: "instant",
      dateLabel: headline,
      timeRangeLabel: "",
      timezoneLabel: "",
      detailLabel:
        "No fixed interview date or time — start or join the room when you are ready.",
    };
  }

  const start = asDate(booking?.scheduled_start_time);
  const end = asDate(booking?.scheduled_end_time);
  const timezoneLabel =
    extractTimezoneFromSlotLabel(booking?.slot_label) ||
    Intl.DateTimeFormat().resolvedOptions().timeZone ||
    "Local time";
  const displayTimezone = resolveDisplayTimezone(timezoneLabel);

  if (!start) {
    return {
      dateLabel: "",
      timeRangeLabel: "",
      timezoneLabel,
      detailLabel: booking?.slot_label || "Schedule unavailable",
    };
  }

  const dateLabel = start.toLocaleDateString("en-IN", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: displayTimezone,
  });
  const timeOptions = {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    timeZone: displayTimezone,
  };
  const startLabel = start.toLocaleTimeString("en-IN", timeOptions);
  const endLabel = end ? end.toLocaleTimeString("en-IN", timeOptions) : "";
  const timeRangeLabel = endLabel ? `${startLabel} - ${endLabel}` : startLabel;
  return {
    dateLabel,
    timeRangeLabel,
    timezoneLabel,
    detailLabel: [dateLabel, timeRangeLabel, timezoneLabel]
      .filter(Boolean)
      .join(" | "),
  };
};

export const isLiveMeetRoomClosed = (booking) => {
  if (!booking) return false;
  const roomStatus = normalizeLower(booking?.live_room_status || booking?.room_status);
  if (ENDED_ROOM_STATUSES.has(roomStatus)) return true;
  return Boolean(booking?.live_room_ended_at || booking?.room_ended_at);
};

const isExpiredNoShowOutcome = (booking) =>
  normalizeLower(booking?.meeting_outcome) === "expired_no_show";

const hadRealMeetingSession = (booking) =>
  Boolean(
    booking?.meeting_had_session ||
      booking?.has_recording === true ||
      (booking?.has_transcript === true &&
        Number(booking?.transcript_segment_count || 0) >= 2)
  );

export const mergeLiveMeetBookingWithRoom = (booking, room) => {
  if (!booking) return booking;
  const liveRoomStatus = String(room?.status || booking?.live_room_status || "").trim();
  const liveRoomEndedAt = room?.ended_at || booking?.live_room_ended_at || null;
  const roomClosed = isLiveMeetRoomClosed({
    ...booking,
    live_room_status: liveRoomStatus,
    live_room_ended_at: liveRoomEndedAt,
  });

  if (!liveRoomStatus && !liveRoomEndedAt && !roomClosed) {
    return booking;
  }

  const noShow = isExpiredNoShowOutcome(booking);
  const hadSession = hadRealMeetingSession(booking);
  const reschedulePending = isInterviewRoomReschedulePending(booking);
  const declined = isInterviewRoomDeclined(booking);

  return {
    ...booking,
    live_room_status: liveRoomStatus || booking?.live_room_status,
    live_room_ended_at: liveRoomEndedAt || booking?.live_room_ended_at,
    meeting_completed: Boolean(
      booking?.meeting_completed || (roomClosed && hadSession && !noShow)
    ),
    can_join:
      roomClosed || noShow || reschedulePending || declined ? false : booking?.can_join,
    join_reason: declined
      ? "declined"
      : reschedulePending
      ? "rescheduled"
      : noShow
        ? "expired"
        : roomClosed
          ? "ended"
          : booking?.join_reason,
    join_message: declined
      ? "This interview was declined."
      : reschedulePending
      ? booking?.meeting_outcome_message ||
        booking?.join_message ||
        "Use Reschedule to pick a new time. The previous link is retired automatically."
      : noShow
      ? booking?.meeting_outcome_message ||
        booking?.join_message ||
        "No one joined this interview before the scheduled time ended."
      : roomClosed
        ? "This interview has ended."
        : booking?.join_message,
  };
};

export const isInstantLiveMeetBooking = (booking) =>
  ["instant", "instant_live_meet"].includes(normalizeLower(booking?.scheduling_mode));

export const resolveInterviewRoomStatus = (booking) => {
  const status = normalizeLower(booking?.status);
  const roomStatus = normalizeLower(
    booking?.live_room_status || booking?.room_status
  );
  const joinReason = normalizeLower(booking?.join_reason);
  const meetingOutcome = normalizeLower(booking?.meeting_outcome);
  const isInstantMeet = isInstantLiveMeetBooking(booking);
  const start = asDate(booking?.scheduled_start_time);
  const end = asDate(booking?.scheduled_end_time);
  const joinExpiresAt = asDate(booking?.join_expires_at);
  const hasTranscript =
    booking?.has_transcript === true ||
    Number(booking?.transcript_segment_count || 0) > 0;
  const hadSession = hadRealMeetingSession(booking);
  const now = Date.now();
  const roomClosed = isLiveMeetRoomClosed(booking);
  const reschedulePending = isInterviewRoomReschedulePending(booking);
  const canJoin = Boolean(
    booking?.can_join && getInterviewRoomJoinUrl(booking) && !roomClosed && !reschedulePending
  );

  if (DECLINED_STATUSES.has(status)) {
    const declinedBy = status === "declined_by_candidate" ? "Candidate" : "Interviewer";
    const reason = String(booking?.decline_reason || "").trim();
    return {
      key: "declined",
      label: `Declined by ${declinedBy}`,
      helper: reason ? `Reason: ${reason}` : `This interview was declined by the ${declinedBy.toLowerCase()}.`,
    };
  }

  if (CANCELLED_STATUSES.has(status) || CANCELLED_STATUSES.has(roomStatus)) {
    return {
      key: "cancelled",
      label: "Cancelled",
      helper: "This interview was cancelled.",
    };
  }

  if (reschedulePending) {
    return {
      key: "reschedule_pending",
      label: booking?.meeting_outcome_label || "Reschedule pending",
      helper:
        booking?.meeting_outcome_message ||
        booking?.join_message ||
        "Use Reschedule to pick a new time. The previous link is retired automatically.",
    };
  }

  if (
    meetingOutcome !== "expired_no_show" &&
    (booking?.meeting_completed ||
      COMPLETED_STATUSES.has(status) ||
      (hadSession &&
        (COMPLETED_STATUSES.has(roomStatus) || roomClosed)))
  ) {
    return {
      key: "completed",
      label: "Completed",
      helper: "This interview has ended.",
    };
  }

  const acceptanceConfig = ACCEPTANCE_STATUS_CONFIG[status];
  if (acceptanceConfig) {
    const actionLabel = String(booking?.action_status_label || "").trim();
    return {
      key: acceptanceConfig.key,
      label:
        actionLabel ||
        booking?.meeting_outcome_label ||
        acceptanceConfig.label,
      helper: booking?.meeting_outcome_message || acceptanceConfig.helper,
    };
  }

  if (
    meetingOutcome === "expired_no_show" ||
    (joinReason === "expired" && !hadSession && !canJoin)
  ) {
    return {
      key: "not_joined",
      label: booking?.meeting_outcome_label || "Not joined",
      helper:
        booking?.meeting_outcome_message ||
        booking?.join_message ||
        "No one joined this interview before the scheduled time ended.",
    };
  }

  if (
    meetingOutcome !== "expired_no_show" &&
    (booking?.meeting_completed ||
      (hadSession &&
        (COMPLETED_STATUSES.has(status) ||
          COMPLETED_STATUSES.has(roomStatus) ||
          roomClosed)))
  ) {
    return {
      key: "completed",
      label: "Completed",
      helper: "This interview has ended.",
    };
  }

  if (canJoin) {
    return {
      key: "active",
      label: isInstantMeet ? INSTANT_LIVE_MEET_LABEL : LIVE_MEET_ACTIVE_LABEL,
      helper: isInstantMeet
        ? `${INSTANT_LIVE_MEET_LABEL} - join anytime, no fixed schedule.`
        : "Room controls are ready now.",
    };
  }

  if (
    !isInstantMeet &&
    (joinReason === "expired" || (joinExpiresAt && now > joinExpiresAt.getTime()))
  ) {
    if (hadSession || booking?.meeting_completed) {
      return {
        key: "completed",
        label: "Completed",
        helper: "This interview has ended.",
      };
    }
    return {
      key: "not_joined",
      label: booking?.meeting_outcome_label || "Time expired",
      helper:
        booking?.meeting_outcome_message ||
        booking?.join_message ||
        "The interview join window has passed.",
    };
  }

  if (start) {
    const diffMs = start.getTime() - now;
    if (Math.abs(diffMs) <= STARTING_SOON_WINDOW_MS) {
      return {
        key: "starting_soon",
        label: "Starting Soon",
        helper: "Join opens when the interview window begins.",
      };
    }
  }

  if (!isInstantMeet && end && now > end.getTime()) {
    if (hadSession || booking?.meeting_completed) {
      return {
        key: "completed",
        label: "Completed",
        helper: "This interview has ended.",
      };
    }
    return {
      key: "not_joined",
      label: booking?.meeting_outcome_label || "Time expired",
      helper:
        booking?.meeting_outcome_message ||
        booking?.join_message ||
        "The scheduled interview window has passed.",
    };
  }

  const actionLabel = String(booking?.action_status_label || "").trim();
  if (status === "confirmed") {
    return {
      key: "confirmed",
      label: actionLabel || booking?.meeting_outcome_label || "Confirmed",
      helper:
        booking?.meeting_outcome_message ||
        "This interview is confirmed. Join opens closer to the scheduled time.",
    };
  }

  return {
    key: "scheduled",
    label: actionLabel || booking?.meeting_outcome_label || "Scheduled",
    helper:
      booking?.meeting_outcome_message ||
      "This interview room is scheduled and not open yet.",
  };
};

export const getInterviewRoomStatusBadgeClassName = (statusKey) => {
  switch (statusKey) {
    case "active":
      return "border-emerald-200 bg-emerald-100 text-emerald-800";
    case "starting_soon":
      return "border-amber-200 bg-amber-100 text-amber-800";
    case "completed":
      return "border-slate-200 bg-slate-100 text-slate-700";
    case "cancelled":
      return "border-rose-200 bg-rose-100 text-rose-700";
    case "declined":
      return "border-rose-200 bg-rose-100 text-rose-700";
    case "reschedule_pending":
      return "border-amber-200 bg-amber-100 text-amber-800";
    case "awaiting_confirmation":
      return "border-amber-200 bg-amber-100 text-amber-800";
    case "confirmed_by_candidate":
    case "confirmed_by_interviewer":
    case "confirmed":
      return "border-emerald-200 bg-emerald-100 text-emerald-800";
    case "expired":
    case "not_joined":
      return "border-orange-200 bg-orange-100 text-orange-700";
    default:
      return "border-sky-200 bg-sky-100 text-sky-800";
  }
};

export const getInterviewRoomStatusMessage = (booking) => {
  const resolved = resolveInterviewRoomStatus(booking);
  const joinReason = normalizeLower(booking?.join_reason);
  const joinMessage = String(booking?.join_message || "").trim();

  if (resolved.key === "declined") {
    return resolved.helper;
  }

  // Backend used to map pending_acceptance to join_reason=cancelled — don't show that
  // when the UI status is still scheduled/starting_soon/active.
  if (joinReason === "cancelled" && resolved.key !== "cancelled") {
    return resolved.helper;
  }
  if (joinMessage && joinReason !== "cancelled") {
    return joinMessage;
  }
  return joinMessage || resolved.helper;
};

/** Message for the join-unavailable alert — never show false "cancelled". */
export const getJoinUnavailableAlertMessage = (booking) => {
  const resolved = resolveInterviewRoomStatus(booking);
  const joinReason = normalizeLower(booking?.join_reason);
  const joinMessage = String(booking?.join_message || "").trim();

  if (resolved.key === "declined") {
    return resolved.helper;
  }

  if (resolved.key === "not_joined" || resolved.key === "expired") {
    return resolved.helper;
  }
  if (resolved.key === "reschedule_pending") {
    return resolved.helper;
  }
  if (joinReason === "cancelled" && resolved.key !== "cancelled") {
    return resolved.helper;
  }
  if (joinReason === "expired" || joinReason === "ended") {
    return joinMessage || resolved.helper;
  }
  if (joinMessage && joinReason !== "cancelled") {
    return joinMessage;
  }
  return joinMessage || resolved.helper || "Join is not available for this interview.";
};

export const parseInterviewRoomDate = asDate;

const normalizeEmailErrors = (value) =>
  value && typeof value === "object" && !Array.isArray(value) ? value : {};

const asErrorText = (value) => {
  if (typeof value !== "string") return "";
  return value.trim();
};

const isRescheduleEmailUpdate = (booking) =>
  /reschedul/i.test(String(booking?.manual_note || "").trim());

const buildEmailDeliveryEntry = ({ label, errorText, emailStatus }) => {
  if (emailStatus === "sending") {
    return {
      label,
      status: "sending",
      statusLabel: "Sending",
      detail: "Sending in background.",
    };
  }
  if (errorText) {
    return {
      label,
      status: "failed",
      statusLabel: "Failed",
      detail: errorText,
    };
  }
  if (emailStatus === "success" || emailStatus === "partial") {
    return {
      label,
      status: "sent",
      statusLabel: "Sent",
      detail: "Delivered successfully.",
    };
  }
  if (emailStatus === "failed") {
    return {
      label,
      status: "failed",
      statusLabel: "Failed",
      detail: "Delivery failed.",
    };
  }
  return {
    label,
    status: "unknown",
    statusLabel: "Unknown",
    detail: "Status unavailable.",
  };
};

export const getInterviewRoomEmailStatusBadgeClassName = (status) => {
  switch (status) {
    case "sent":
      return "border-emerald-200 bg-emerald-50 text-emerald-700";
    case "failed":
      return "border-rose-200 bg-rose-50 text-rose-700";
    case "sending":
      return "border-amber-200 bg-amber-50 text-amber-700";
    default:
      return "border-slate-200 bg-slate-50 text-slate-600";
  }
};

export const getInterviewRoomEmailDelivery = (booking) => {
  if (!booking) return null;
  const emailStatus = normalizeLower(booking?.email_status);
  const emailErrors = normalizeEmailErrors(booking?.email_errors);
  const candidateError = asErrorText(emailErrors.candidate);
  const interviewerError = asErrorText(emailErrors.interviewer);

  if (!emailStatus && !candidateError && !interviewerError) {
    return null;
  }

  const notificationLabel = isRescheduleEmailUpdate(booking)
    ? "Reschedule email"
    : "Interview email";
  const entries = [
    buildEmailDeliveryEntry({
      label: "Candidate email",
      errorText: candidateError,
      emailStatus,
    }),
    buildEmailDeliveryEntry({
      label: "Interviewer email",
      errorText: interviewerError,
      emailStatus,
    }),
  ];
  const failedCount = entries.filter((entry) => entry.status === "failed").length;
  const sendingCount = entries.filter((entry) => entry.status === "sending").length;

  let summaryStatus = "unknown";
  let summaryLabel = "Email status unavailable";
  let helperText = "Email delivery details are not available yet.";

  if (emailStatus === "sending" || sendingCount > 0) {
    summaryStatus = "sending";
    summaryLabel = "Emails sending";
    helperText = `${notificationLabel}s are being sent in the background.`;
  } else if (failedCount === 0 && emailStatus === "success") {
    summaryStatus = "sent";
    summaryLabel = "Emails sent";
    helperText = "Candidate and interviewer emails were sent successfully.";
  } else if (failedCount === 1 || emailStatus === "partial") {
    summaryStatus = "failed";
    summaryLabel = "1 email failed";
    helperText = "One recipient did not receive the latest email update.";
  } else if (failedCount >= 2 || emailStatus === "failed") {
    summaryStatus = "failed";
    summaryLabel = `${notificationLabel} failed`;
    helperText = "The latest booking email update could not be delivered.";
  }

  return {
    title: `${notificationLabel} delivery`,
    summaryStatus,
    summaryLabel,
    helperText,
    entries,
  };
};

const normalizeRescheduleTimeValue = (value) => normalizeRescheduleTimeLabel(value);

const normalizeRescheduleSuggestion = (suggestion, index = 0) => {
  const label = String(suggestion?.label || "").trim();
  const slotValue = String(suggestion?.slot_value || "").trim();
  const timezone = resolveRescheduleTimezoneName(
    suggestion?.timezone || DEFAULT_MEETING_TIMEZONE
  );
  let date = String(suggestion?.date || "").trim();
  let startTime = normalizeRescheduleTimeValue(
    suggestion?.start_time || suggestion?.startTime
  );
  let endTime = normalizeRescheduleTimeValue(
    suggestion?.end_time || suggestion?.endTime
  );

  if ((!date || !startTime) && label) {
    const parsed = parseRescheduleSuggestionLabel(label, timezone);
    if (parsed?.date && parsed?.start_time) {
      date = parsed.date;
      startTime = parsed.start_time;
      endTime = endTime || parsed.end_time || "";
      if (parsed.timezone) {
        return {
          id: String(suggestion?.id || slotValue || label || `suggestion-${index}`),
          label: label || slotValue || "Suggested time",
          slot_value: slotValue,
          timezone: resolveRescheduleTimezoneName(parsed.timezone, timezone),
          date,
          start_time: startTime,
          end_time: endTime,
        };
      }
    }
  }

  return {
    id: String(suggestion?.id || slotValue || label || `suggestion-${index}`),
    label: label || slotValue || "Suggested time",
    slot_value: slotValue,
    timezone,
    date,
    start_time: startTime,
    end_time: endTime,
  };
};

const extractSuggestionsFromTimeline = (booking) => {
  const bookingId = String(booking?.booking_id || "").trim();
  const events = Array.isArray(booking?.timeline) ? booking.timeline : [];
  const matches = events.filter(
    (event) =>
      String(event?.booking_id || "").trim() === bookingId &&
      normalizeLower(event?.event_type) === "reschedule_requested"
  );
  if (!matches.length) return [];
  const latest = matches[matches.length - 1];
  const eventData = latest?.event_data || {};
  const structured = Array.isArray(eventData?.suggested_slots)
    ? eventData.suggested_slots
    : [];
  if (structured.length) {
    return structured.map(normalizeRescheduleSuggestion);
  }
  const labels = Array.isArray(eventData?.available_slots)
    ? eventData.available_slots
    : [];
  const notes = String(eventData?.candidate_availability_notes || "").trim();
  const lines = [
    ...labels.map((item) => String(item || "").trim()).filter(Boolean),
    ...notes.split(/\r?\n/).map((item) => item.trim()).filter(Boolean),
  ];
  return lines.map((line, index) =>
    normalizeRescheduleSuggestion({ label: line }, index)
  );
};

export const getRescheduleSuggestedSlots = (booking) => {
  if (!booking) return [];
  const fromApi = Array.isArray(booking?.reschedule_suggested_slots)
    ? booking.reschedule_suggested_slots
    : [];
  if (fromApi.length) {
    return fromApi.map(normalizeRescheduleSuggestion);
  }
  return extractSuggestionsFromTimeline(booking);
};

export const getLiveInterviewRecordingListItemState = (recording) => {
  const status = String(recording?.status || "").trim().toLowerCase();
  const failureReason = String(
    recording?.egress_error || recording?.error_message || ""
  ).trim();
  const isFailed = status === "failed";
  return {
    status,
    isFailed,
    failureReason,
    canDownload: !isFailed && Boolean(recording?.playback_available),
    statusLabel: isFailed ? "Failed" : recording?.status || "available",
  };
};
